import { QUOTE_EXPIRATION_DAYS } from '@/app/config/quotes.config';
import { getQuoteStatus } from '@/shared/utils/get-quote-status..util';
import {
  ChevronDown,
  FileText,
  Gift,
  Home,
  Shield,
  User,
  Wallet,
} from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { useQuoteSummary } from '../context/quote-summary.context';
import { useInsurerCompanies } from '../hooks/common/use-insurer-companies.hook';

const DEFAULT_VALUE = '-';

const propertyTypeLabel: Record<string, string> = {
  house: 'Casa',
  apartment: 'Apartamento',
};

export function QuoteSummary() {
  const { summary } = useQuoteSummary();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { data: insurerCompanies } = useInsurerCompanies();

  // O form guarda o id da congênere (SeguradoraAnterior); exibimos o nome.
  const congenerLabel = useMemo(() => {
    const raw = summary.insurance.congener;
    if (!raw) return raw;
    const company = insurerCompanies?.find(c => String(c.id) === String(raw));
    return company?.name ?? raw;
  }, [summary.insurance.congener, insurerCompanies]);

  const premiumStatusLabel = useMemo(() => {
    const rawStatus = summary.premium.status?.trim();

    if (!rawStatus) return '';

    const parsedStatus = Number(rawStatus);

    if (!Number.isNaN(parsedStatus)) {
      return getQuoteStatus(parsedStatus).label;
    }

    return rawStatus;
  }, [summary.premium.status]);

  const insuranceTypeLabel = useMemo(() => {
    if (summary.insurance.insuranceType === 'renewal') {
      return 'Renovação Congênere';
    }
    if (summary.insurance.insuranceType === 'new') {
      return 'Seguro Novo';
    }
    return '';
  }, [summary.insurance.insuranceType]);

  const quoteNumberField = summary.premium.quoteNumber
    ? { label: 'Número da Cotação', value: summary.premium.quoteNumber }
    : null;

  const premiumFields = [
    { label: 'Prêmio Líquido', value: summary.premium.premium },
    { label: 'IOF', value: summary.premium.iof },
    { label: 'Prêmio Total', value: summary.premium.totalPremium },
    { label: 'Produto', value: summary.premium.product },
    { label: 'Status', value: premiumStatusLabel },
  ].filter(field => !!field.value);

  const quoteValidUntil = useMemo(
    () => calculateQuoteExpirationDate(summary.insurance.inclusionDate),
    [summary.insurance.inclusionDate]
  );

  // "Usuário de Inclusão" = e-mail (insertByUserName), só leitura.
  const insuranceFields: Array<{
    label: string;
    value?: string;
    longValue?: boolean;
  }> = [
    {
      label: 'Data da Inclusão',
      value: formatDate(summary.insurance.inclusionDate),
    },
    {
      label: 'Início de Vigência',
      value: formatDate(summary.insurance.validityStart),
    },
    {
      label: 'Fim de Vigência',
      value: formatDate(summary.insurance.validityEnd),
    },
    {
      label: 'Usuário de Inclusão',
      value: summary.insurance.inclusionUser,
      longValue: true,
    },
    {
      label: 'Cotação válida até',
      value: formatDate(quoteValidUntil),
    },
    { label: 'Tipo de Seguro', value: insuranceTypeLabel },
  ].filter(field => !!field.value);

  if (summary.insurance.insuranceType === 'renewal') {
    insuranceFields.push(
      { label: 'Congênere', value: congenerLabel, longValue: true },
      { label: 'Número Apólice', value: summary.insurance.policyNumber }
    );
  }

  const proponentFields = [
    {
      label: 'CPF/CNPJ',
      value: summary.proponent.document,
    },
    {
      label: 'Nome/Razão social',
      value: summary.proponent.name,
    },
    {
      label: 'Nome Social',
      value: summary.proponent.socialName,
    },
  ].filter(field => !!field.value);

  const propertyFields = [
    {
      label: 'Tipo de Imóvel',
      value: summary.property.propertyType
        ? propertyTypeLabel[summary.property.propertyType] ||
          summary.property.propertyType
        : '',
    },
    { label: 'CEP', value: summary.property.cep },
    { label: 'Endereço', value: summary.property.address },
    { label: 'Número', value: summary.property.number },
    { label: 'Complemento', value: summary.property.complement },
    { label: 'Bairro', value: summary.property.neighborhood },
    { label: 'Estado', value: summary.property.state },
    { label: 'Cidade', value: summary.property.city },
    {
      label: 'Tipo de Construção',
      value:
        summary.property.constructionTypeLabel ||
        summary.property.constructionType,
    },
    {
      label: 'Classificação do Imóvel',
      value:
        summary.property.propertyClassificationLabel ||
        summary.property.propertyClassification,
    },
    {
      label: 'Objeto Segurado',
      value: summary.property.insuredObjectTypeLabel || '',
    },
  ].filter(field => !!field.value);

  const assistanceFields = {
    packageName: summary.assistance.packageName,
    assistances: summary.assistance.assistances,
    raffles: summary.assistance.raffles,
  };

  const hasPremiumInfo = premiumFields.length > 0;
  const commercializationFields = [
    {
      label: 'Corretor',
      value: summary.commercialization.brokerName,
      longValue: true,
    },
    {
      label: 'Percentual Corretagem',
      value: summary.commercialization.commissionPercent
        ? `${summary.commercialization.commissionPercent}%`
        : '',
    },
    {
      label: 'Participação',
      value: summary.commercialization.participationPercent
        ? `${summary.commercialization.participationPercent}%`
        : '',
    },
    ...(summary.commercialization.coBrokers || []).flatMap(
      (coBroker, index) => [
        {
          label: `Co-corretor ${index + 1}`,
          value: coBroker.name,
          longValue: true,
        },
        {
          label: `Participação Co-corretor ${index + 1}`,
          value: coBroker.participationPercent
            ? `${coBroker.participationPercent}%`
            : '',
        },
      ]
    ),
  ].filter(field => !!field.value);
  const hasCommercializationInfo = commercializationFields.length > 0;
  const hasInsuranceInfo = insuranceFields.length > 0;
  const hasProponentInfo = proponentFields.length > 0;
  const hasPropertyInfo = propertyFields.length > 0;
  const hasCoverageInfo = summary.coverages.length > 0;
  const hasAssistanceInfo =
    !!assistanceFields.packageName ||
    assistanceFields.assistances.length > 0 ||
    assistanceFields.raffles.length > 0;

  return (
    <aside className='w-full shrink-0 lg:sticky lg:top-6 lg:w-[360px] xl:w-[400px] lg:self-start'>
      <div
        className={`flex flex-col rounded-[24px] border border-alba-gray-200 bg-white shadow-[0px_8px_32px_rgba(12,7,32,0.12)]
          ${isCollapsed ? '' : 'lg:min-h-[615px]'}`}
      >
        <button
          type='button'
          className='flex items-center justify-between gap-2 rounded-t-[24px] border-b border-alba-gray-200 px-6 py-4 text-left'
          onClick={() => setIsCollapsed(prev => !prev)}
        >
          <div className='flex items-center gap-2 text-alba-blue-500'>
            <FileText className='h-4 w-4' strokeWidth={1.6} />
            <span className='text-base font-semibold'>Resumo da Cotação</span>
          </div>
          <ChevronDown
            className={`h-5 w-5 text-alba-blue-500 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
            strokeWidth={1.8}
          />
        </button>

        {!isCollapsed && (
          <div className='flex flex-1 flex-col gap-4 px-4 pb-6 pt-4'>
            {hasPremiumInfo && (
              <SummarySection
                icon={<Wallet className='h-4 w-4' strokeWidth={1.6} />}
                title='Informações do Prêmio'
              >
                <SummaryGrid columns={2}>
                  {quoteNumberField && (
                    <SummaryField
                      key={quoteNumberField.label}
                      label={quoteNumberField.label}
                      value={quoteNumberField.value}
                    />
                  )}
                  {premiumFields.map(field => (
                    <SummaryField
                      key={field.label}
                      label={field.label}
                      value={field.value}
                    />
                  ))}
                </SummaryGrid>
              </SummarySection>
            )}

            <SummarySection
              icon={<Shield className='h-4 w-4' strokeWidth={1.6} />}
              title='Dados do Seguro'
            >
              {hasInsuranceInfo && (
                <SummaryGrid columns={2}>
                  {insuranceFields.map(field => (
                    <SummaryField
                      key={field.label}
                      label={field.label}
                      value={field.value}
                      longValue={field.longValue}
                    />
                  ))}
                </SummaryGrid>
              )}
            </SummarySection>

            <SummarySection
              icon={<User className='h-4 w-4' strokeWidth={1.6} />}
              title='Proponente'
            >
              {hasProponentInfo && (
                <SummaryGrid columns={1}>
                  {proponentFields.map(field => (
                    <SummaryField
                      key={field.label}
                      label={field.label}
                      value={field.value}
                    />
                  ))}
                </SummaryGrid>
              )}
            </SummarySection>

            <SummarySection
              icon={<Home className='h-4 w-4' strokeWidth={1.6} />}
              title='Dados do Imóvel'
            >
              {hasPropertyInfo && (
                <SummaryGrid columns={1}>
                  {propertyFields.map(field => (
                    <SummaryField
                      key={field.label}
                      label={field.label}
                      value={field.value}
                    />
                  ))}
                </SummaryGrid>
              )}
            </SummarySection>

            <SummarySection
              icon={<FileText className='h-4 w-4' strokeWidth={1.6} />}
              title='Coberturas'
            >
              {hasCoverageInfo && (
                <div className='flex flex-col gap-3'>
                  {summary.coverages.map(coverage => (
                    <div
                      key={coverage.id}
                      className='rounded-xl border border-alba-gray-200 p-3'
                    >
                      <div className='flex items-center justify-between text-xs font-semibold text-alba-gray-700'>
                        <span>{coverage.title}</span>
                        <span className='text-alba-blue-500'>
                          {formatValue(coverage.amountLabel)}
                        </span>
                      </div>
                      {coverage.franchiseLabel && (
                        <p className='text-[11px] text-alba-gray-500'>
                          Franquia: {formatValue(coverage.franchiseLabel)}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </SummarySection>

            {hasAssistanceInfo && (
              <SummarySection
                icon={<Gift className='h-4 w-4' strokeWidth={1.6} />}
                title='Assistência/Sorteio'
              >
                <SummaryField
                  label='Pacote Assistência'
                  value={assistanceFields.packageName}
                />
                <SummaryList
                  label='Assistência'
                  items={assistanceFields.assistances}
                />
                <SummaryList
                  label='Capitalização/Sorteio'
                  items={assistanceFields.raffles}
                />
              </SummarySection>
            )}

            {hasCommercializationInfo && (
              <SummarySection
                icon={<FileText className='h-4 w-4' strokeWidth={1.6} />}
                title='Comercialização'
              >
                <SummaryGrid columns={1}>
                  {commercializationFields.map(field => (
                    <SummaryField
                      key={field.label}
                      label={field.label}
                      value={field.value}
                      longValue={field.longValue}
                    />
                  ))}
                </SummaryGrid>
              </SummarySection>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}

type SummarySectionProps = {
  icon: ReactNode;
  title: string;
  children: ReactNode;
};

function SummarySection({ icon, title, children }: SummarySectionProps) {
  return (
    <section className='flex flex-col gap-3 rounded-2xl border border-alba-gray-200 p-4'>
      <header className='flex items-center gap-2'>
        <div className='flex h-6 w-6 items-center justify-center rounded-full bg-[rgba(44,40,100,0.1)] text-alba-blue-500'>
          {icon}
        </div>
        <span className='text-sm font-semibold text-alba-blue-500'>
          {title}
        </span>
      </header>
      <div className='flex flex-col gap-3'>{children}</div>
    </section>
  );
}

type SummaryGridProps = {
  children: ReactNode;
  columns?: 1 | 2;
};

function SummaryGrid({ children, columns = 1 }: SummaryGridProps) {
  const columnClasses =
    columns === 2
      ? 'grid grid-cols-1 gap-3 sm:grid-cols-2'
      : 'grid grid-cols-1 gap-3';

  return <div className={columnClasses}>{children}</div>;
}

type SummaryFieldProps = {
  label: string;
  value?: string;
  /** E-mails / strings longas: quebra dentro da coluna sem estourar o card */
  longValue?: boolean;
};

function SummaryField({ label, value, longValue }: SummaryFieldProps) {
  const resolved =
    typeof value === 'string'
      ? value
      : value !== undefined
        ? String(value)
        : '';
  if (resolved.trim() === '') return null;

  return (
    <div className='flex min-w-0 flex-row items-center justify-between gap-3 sm:flex-col sm:items-start sm:justify-start sm:gap-1'>
      <span className='shrink-0 text-sm font-semibold text-alba-black-500 sm:text-[11px] sm:uppercase sm:tracking-wide sm:text-alba-gray-500'>
        {label}
      </span>
      <span
        className={
          longValue
            ? 'min-w-0 wrap-anywhere text-right text-sm leading-snug text-alba-gray-900 sm:text-left'
            : 'min-w-0 truncate text-right text-sm text-alba-gray-900 sm:text-left'
        }
        title={longValue ? resolved : undefined}
      >
        {formatValue(resolved)}
      </span>
    </div>
  );
}

type SummaryListProps = {
  label: string;
  items: string[];
};

function SummaryList({ label, items }: SummaryListProps) {
  const hasItems = items && items.length > 0;

  if (!hasItems) {
    return null;
  }

  return (
    <div className='flex flex-col gap-1'>
      <span className='text-[11px] font-semibold uppercase tracking-wide text-alba-gray-500'>
        {label}
      </span>
      {hasItems ? (
        <ul className='list-disc space-y-1 pl-4 text-sm text-alba-gray-900'>
          {items.map(item => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : (
        <span className='text-sm text-alba-gray-500'>{DEFAULT_VALUE}</span>
      )}
    </div>
  );
}

function formatValue(value?: string) {
  return value || '';
}

function formatDate(value?: string) {
  const resolved =
    typeof value === 'string'
      ? value
      : value !== undefined
        ? String(value)
        : '';
  if (resolved.trim() === '') return '';
  const dateOnlyMatch = resolved.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const date = dateOnlyMatch
    ? new Date(
        Number(dateOnlyMatch[1]),
        Number(dateOnlyMatch[2]) - 1,
        Number(dateOnlyMatch[3])
      )
    : new Date(resolved);
  if (Number.isNaN(date.getTime())) {
    return resolved;
  }
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function calculateQuoteExpirationDate(inclusionDate?: string) {
  const baseDate = parseDateValue(inclusionDate);

  if (!baseDate) {
    return '';
  }

  const expirationDate = new Date(baseDate);
  expirationDate.setDate(expirationDate.getDate() + QUOTE_EXPIRATION_DAYS);

  return expirationDate.toISOString();
}

function parseDateValue(value?: string) {
  const resolved =
    typeof value === 'string'
      ? value.trim()
      : value !== undefined
        ? String(value).trim()
        : '';

  if (!resolved) {
    return null;
  }

  const dateOnlyMatch = resolved.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    return new Date(
      Number(dateOnlyMatch[1]),
      Number(dateOnlyMatch[2]) - 1,
      Number(dateOnlyMatch[3]),
      12
    );
  }

  const parsedDate = new Date(resolved);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return new Date(
    parsedDate.getFullYear(),
    parsedDate.getMonth(),
    parsedDate.getDate(),
    12
  );
}
